import React, { useState, useEffect, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../../../context/FamilyContext';
import { UserContext } from '../../../context/UserContext';
import MyTextInput from '../../basic/MyTextInput';
import MyDropdownGeneric from '../../basic/MyDropdownGeneric';
import MyButton from '../../basic/MyButton';
import MyConfirmationModal from '../../basic/MyConfirmationModal';
import Notification from '../../Notification/Notification';
import { currencies } from '../../basic/currencies';
import { timezones } from '../../basic/timezones';
import { updateFamilySettings, deleteFamilyAccount } from '../../../api';
import './FamilySettingsView.css';

const FamilySettingsView = () => {
    const navigate = useNavigate();
    const { logout } = useContext(UserContext);
    const { familyId, familyName, currencyCode, timezone, refetchFamilyData } = useFamily();
    const [formData, setFormData] = useState({
        familyName: '',
        currency: '',
        timezone: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showNotification, setShowNotification] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deletionStatus, setDeletionStatus] = useState({
        show: false,
        message: '',
        type: 'info'
    });

    const originalData = useMemo(() => ({
        familyName: familyName || '',
        currency: currencyCode ? `${currencyCode} - ${currencies.find(c => c.code === currencyCode)?.name || ''} - ${currencies.find(c => c.code === currencyCode)?.symbol || ''}`  : '',
        timezone: timezone ? `${timezone} - ${timezones.find(t => t.code === timezone)?.name || ''}` : ''
    }), [familyName, currencyCode, timezone]);

    useEffect(() => {
        setFormData(originalData);
    }, [originalData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDropdownChange = (name) => (value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const isFormChanged = () => {
        return JSON.stringify(formData) !== JSON.stringify(originalData);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isFormChanged()) {
            setIsLoading(true);
            setError(null);
            
            const formattedData = {
                family_name: formData.familyName,
                currency_code: formData.currency.split(' - ')[0],
                timezone: formData.timezone.split(' - ')[0]
            };

            try {
                await updateFamilySettings(familyId, formattedData);
                await refetchFamilyData();
                console.log('Family settings updated successfully');
                setShowNotification(true);
            } catch (err) {
                setError('Failed to update family settings. Please try again.');
                console.error('Error updating family settings:', err);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleCloseNotification = () => {
        setShowNotification(false);
    };

    const handleDeletionNotificationClose = () => {
        setDeletionStatus(prev => ({ ...prev, show: false }));
    };

    const handleDeleteClick = () => {
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleting(true);
        setError(null);
        setIsDeleteModalOpen(false); // Close the modal immediately
        
        // Show initial deletion notification
        setDeletionStatus({
            show: true,
            message: 'Deleting family account...',
            type: 'info'
        });

        try {
            await deleteFamilyAccount(familyId);
            
            // Show success notification
            setDeletionStatus({
                show: true,
                message: 'Family account deleted successfully. Redirecting...',
                type: 'success'
            });

            // Wait for notification to be visible before logout/redirect
            setTimeout(() => {
                logout();
                navigate('/auth/login');
            }, 2000);

        } catch (err) {
            console.error('Error deleting family account:', err);
            setDeletionStatus({
                show: true,
                message: 'Failed to delete family account. Please try again.',
                type: 'error'
            });
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="family-settings-container">
                <h2>Family Settings</h2>
                {error && <div className="error-message">{error}</div>}
                <form onSubmit={handleSubmit}>
                    <MyTextInput 
                        label="Family Name" 
                        name="familyName" 
                        value={formData.familyName} 
                        onChange={handleInputChange} 
                        required 
                    />
                    <MyDropdownGeneric
                        label="Currency"
                        options={currencies.map(c => `${c.code} - ${c.name} - ${c.symbol}`)}
                        value={formData.currency}
                        onChange={handleDropdownChange('currency')}
                    />
                    <MyDropdownGeneric
                        label="Timezone"
                        options={timezones.map(t => `${t.code} - ${t.name}`)}
                        value={formData.timezone}
                        onChange={handleDropdownChange('timezone')}
                    />
                    <MyButton 
                        text={isLoading ? "Saving..." : "Save Changes"} 
                        type="submit" 
                        disabled={!isFormChanged() || isLoading} 
                    />
                </form>
                {showNotification && (
                    <Notification 
                        message="Family settings updated successfully!" 
                        type="success" 
                        onClose={handleCloseNotification} 
                    />
                )}
            </div>

            <div className="family-settings-container danger-zone">
                <h2>Delete Family Account</h2>
                <p className="warning-text">
                    Warning: Deleting your family account will permanently remove all family data, 
                    including all children's accounts, transaction history, and settings. 
                    This action cannot be undone or recovered.
                </p>
                <MyButton 
                    text={isDeleting ? "Deleting..." : "Delete Family Account"} 
                    onClick={handleDeleteClick}
                    disabled={isDeleting}
                    backgroundColor="#dc3545"
                    color="white"
                    hoverBackgroundColor="#c82333"
                    hoverColor="white"
                />
            </div>

            <MyConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Family Account"
                message="Are you sure you want to delete your family account? This will permanently delete all family data, including children's accounts, transaction history, and settings. This action cannot be undone."
            />

            {deletionStatus.show && (
                <Notification 
                    message={deletionStatus.message}
                    type={deletionStatus.type}
                    onClose={handleDeletionNotificationClose}
                />
            )}
        </>
    );
};

export default FamilySettingsView;